import { ScanSearch } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EnrichmentRunPanel,
  useEnrichmentAttributeGroups,
} from '~/components/common/enrichment-run-panel';
import { Button } from '~/components/ui/button';
import type { AppliedProductFilters } from '~/features/experiments/types/product-metadata.types';
import { useLaunchFeedAudit } from '~/features/feed-audit/hooks/use-launch-feed-audit';

interface CatalogFeedAuditButtonProps {
  /** Selected sync/revision (null = latest); reused to skip re-import. */
  syncId: string | null;
  /** Applied catalog filters; empty object = audit the whole (small) catalog. */
  filters: AppliedProductFilters;
  /** Products the current scope covers — shown in the panel header/footer. */
  skuCount: number;
}

/**
 * "Run Feed Audit" action for the Product Catalog, shown only when the scoped
 * product count is within the audit cap. Rendered as a pill inside the page's
 * floating action bar (positioning owned by the parent), it opens the shared
 * enrichment-run panel and fires a FEED_AUDIT run over the current scope
 * (GMC-only, super-admin — gated at the call site), reusing the same launch hook
 * as the Feed Audit page.
 */
export function CatalogFeedAuditButton({ syncId, filters, skuCount }: CatalogFeedAuditButtonProps) {
  const { t } = useTranslation('product-catalog');
  const [open, setOpen] = useState(false);
  // Always enabled (not gated on `open`) so it's warm by the time the panel
  // opens — the underlying query is `staleTime`/`gcTime: Infinity` (baked
  // into the agent image), so fetching it on mount costs one request per session.
  const attributeGroups = useEnrichmentAttributeGroups(true);
  const { launch, isPending } = useLaunchFeedAudit();

  // Names the scope the audit will cover — the filtered subset when filters are
  // on, the whole catalog otherwise.
  const scopeLabel =
    Object.keys(filters).length > 0
      ? t('runFeedAudit.scope.filtered')
      : t('runFeedAudit.scope.all');

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="cursor-pointer gap-2 rounded-full px-5"
        onClick={() => {
          setOpen(true);
        }}
      >
        <ScanSearch aria-hidden="true" className="h-4 w-4" />
        {t('runFeedAudit.cta')}
      </Button>

      <EnrichmentRunPanel
        open={open}
        onOpenChange={setOpen}
        skuCount={skuCount}
        scopeLabel={scopeLabel}
        groups={attributeGroups.groups}
        isLoading={attributeGroups.isLoading}
        isError={attributeGroups.isError}
        onRetry={attributeGroups.refetch}
        isSubmitting={isPending}
        onSubmit={(selection) => {
          launch(selection, { syncId, filters }, () => {
            setOpen(false);
          });
        }}
      />
    </>
  );
}
